import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../api';
import './TagInput.css';

function parseTags(value) {
  if (!value || typeof value !== 'string') return [];
  return value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

function serializeTags(tags) {
  return tags.join(', ');
}

export default function TagInput({ value, onChange, placeholder = 'Add tags…' }) {
  const [tags, setTags] = useState(() => parseTags(value));
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const emitChange = useCallback(
    (newTags) => {
      setTags(newTags);
      onChange(serializeTags(newTags));
    },
    [onChange]
  );

  useEffect(() => {
    setTags(parseTags(value));
  }, [value]);

  useEffect(() => {
    if (!input.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const q = input.trim();
    api
      .getSilent(`/api/tags.php?q=${encodeURIComponent(q)}`)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const filtered = list.filter((t) => !tags.includes(t));
        setSuggestions(filtered.slice(0, 10));
        setShowSuggestions(filtered.length > 0);
        setHighlightIndex(-1);
      })
      .catch(() => {
        setSuggestions([]);
        setShowSuggestions(false);
      });
  }, [input, tags]);

  const addTag = (tag) => {
    const t = typeof tag === 'string' ? tag.trim() : input.trim();
    if (!t || tags.includes(t)) return;
    const next = [...tags, t];
    emitChange(next);
    setInput('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeTag = (index) => {
    const next = tags.filter((_, i) => i !== index);
    emitChange(next);
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((i) => (i < suggestions.length - 1 ? i + 1 : i));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((i) => (i > 0 ? i - 1 : -1));
        return;
      }
      if (e.key === 'Enter' && highlightIndex >= 0 && suggestions[highlightIndex]) {
        e.preventDefault();
        addTag(suggestions[highlightIndex]);
        return;
      }
    }
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
      return;
    }
    if (e.key === 'Backspace' && !input && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div className="tag-input-wrapper">
      <div className="tag-input-chips">
        {tags.map((tag, i) => (
          <span key={i} className="tag-chip">
            {tag}
            <button type="button" className="tag-chip-remove" onClick={() => removeTag(i)} title="Remove tag" aria-label={`Remove ${tag}`}>
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="tag-input-field"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => input && setShowSuggestions(suggestions.length > 0)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={tags.length === 0 ? placeholder : ''}
          autoComplete="off"
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul ref={listRef} className="tag-suggestions" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={s}
              role="option"
              aria-selected={i === highlightIndex}
              className={`tag-suggestion-item ${i === highlightIndex ? 'highlighted' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(s);
              }}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
